import { cx } from "class-variance-authority";
import { useData } from "@/stores/data";
import { appVersion } from "@/helpers/h";
import { X } from "lucide-react";

import AuthHelpers from "../../helpers/AuthHelpers";
import SidebarSD from "./SidebarSD";
import SidebarROC from "./SidebarROC";
import SidebarClient from "./SidebarClient";
import SidebarAdmin from "./SidebarAdmin";
import SidebarServiceEngineer from "./SidebarServiceEngineer";
import SidebarLogisticStaff from "./SidebarLogisticStaff";
import SidebarManager from "./SidebarManager";
import SidebarRepairCenter from "./SidebarRepairCenter";
import SidebarCommVendor from "./SidebarCommVendor";
import SidebarFirstLevelMaintenance from "./SidebarFirstLevelMaintenance";

export default function Sidebar() {
  const { sidebarMinMax } = useData();
  const { sidebarState, setSidebarState } = useData();
  const app = AuthHelpers.auth_role();

  // Map user roles to app types for sidebar display
  const getAppType = () => {
    if (app === "admin") return "admin";
    if (app === "service_desk") return "service_desk";
    if (app === "remote_operation_center") return "remote_operation_center";
    if (app === "client") return "client";
    if (app === "service_engineer") return "service_engineer";
    if (app === "logistic_staff") return "logistic_staff";
    if (app === "manager") return "manager";
    if (app === "repair_center") return "repair_center";
    if (app === "comm_vendor") return "comm_vendor";
    if (app === "first_level_maintenance") return "first_level_maintenance";
    return app;
  };

  const appType = getAppType();

  return (
    <div className="contents">
      {/* Mobile Backdrop */}
      {sidebarState && (
        <div
          className="xl:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarState(false)}
        />
      )}

      <div
        className={cx(
          "bg-gradient-to-b from-white to-gray-50",
          "top-0 left-0 z-50 fixed xl:static",
          "flex flex-col font-medium",
          "overflow-x-hidden overflow-y-auto",
          "transition-all duration-300 ease-in-out",
          "xl:h-full border-r border-gray-200/50",
          !sidebarMinMax ? "xl:p-6 xl:min-w-[320px]" : "xl:p-2 xl:min-w-[80px]",
          !sidebarState
            ? "hidden xl:flex xl:opacity-100 xl:h-full xl:w-auto"
            : "flex p-6 h-screen w-[280px] opacity-100 xl:opacity-100 xl:h-full xl:w-auto",
        )}
      >
        {/* Mobile Close Button */}
        <button
          className={cx(
            "xl:hidden",
            "block",
            "mb-6",
            "p-2",
            "rounded-lg",
            "hover:bg-gray-100",
            "transition-colors",
            "duration-200",
            "self-start",
          )}
          onClick={() => {
            setSidebarState(!sidebarState);
          }}
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Sidebar Content */}
        <div className="flex-1 space-y-2 flex flex-col gap-2">
          {appType == "service_desk" && (
            <SidebarSD sidebarMinMax={sidebarMinMax} />
          )}
          {appType == "remote_operation_center" && (
            <SidebarROC sidebarMinMax={sidebarMinMax} />
          )}
          {appType == "client" && (
            <SidebarClient sidebarMinMax={sidebarMinMax} />
          )}
          {appType == "admin" && <SidebarAdmin sidebarMinMax={sidebarMinMax} />}
          {appType == "service_engineer" && (
            <SidebarServiceEngineer sidebarMinMax={sidebarMinMax} />
          )}
          {appType == "logistic_staff" && (
            <SidebarLogisticStaff sidebarMinMax={sidebarMinMax} />
          )}
          {appType == "manager" && (
            <SidebarManager sidebarMinMax={sidebarMinMax} />
          )}
          {appType == "repair_center" && (
            <SidebarRepairCenter sidebarMinMax={sidebarMinMax} />
          )}
          {appType == "comm_vendor" && (
            <SidebarCommVendor sidebarMinMax={sidebarMinMax} />
          )}
          {appType == "first_level_maintenance" && (
            <SidebarFirstLevelMaintenance sidebarMinMax={sidebarMinMax} />
          )}

          {/* Fallback for unknown roles */}
          {!appType && (
            <div className="p-4 text-center text-gray-500 whitespace-nowrap">
              <p className="whitespace-nowrap">No sidebar content available</p>
              <p className="text-sm whitespace-nowrap">
                Role: {app || "Unknown"}
              </p>
              <p className="text-sm whitespace-nowrap">
                App Type: {appType || "Unknown"}
              </p>
            </div>
          )}
        </div>

        {/* Sidebar Footer */}
        <div className="mt-auto pt-6 border-t border-gray-200">
          {!sidebarMinMax && (
            <div className="space-y-3">
              <div className="px-4 py-2 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 font-medium whitespace-nowrap">
                  Version
                </p>
                <p className="text-sm text-gray-700 font-semibold whitespace-nowrap">
                  {appVersion}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
